/**
 * Assistant rail (frontend-ux-design.md §4.3 / frontend-display.md §3.1).
 *
 * The right-side shell column. Mounted once at the AppLayout shell so the
 * conversation survives route changes. Open state + active scope come from
 * `useAssistantState` (persisted to localStorage; toggled by the Header's
 * Sparkles button + ⌘J via the `aos:toggle-assistant` event).
 *
 *   - `lg`+  : a wide third column (`~480px`, growing to `~560px` at `xl`)
 *              separated from <main> by ONE `border-l` (a structural fold).
 *              <main> reflows when it opens/closes. The generous width keeps
 *              message bubbles + tool-call cards from clipping.
 *   - `<lg`  : an overlay `Sheet` (up to `~560px` / `92vw`) so <main> never
 *              reflows on tablet/mobile and the content never gets cut off.
 *
 * The two presentations are mutually exclusive (driven by a `lg` media query),
 * so the Sheet's overlay never shows on desktop and <main> never reflows on
 * mobile.
 *
 * The panel hosts the live assistant: a real chat thread backed by
 * `POST /api/assistant/chat` (streaming), entity-read tools, and the
 * Approve/Reject affordance for proposed transitions.
 */
import * as React from "react";

import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { AssistantPanel } from "@/components/assistant/AssistantPanel";
import {
  useAssistantState,
  type AssistantState,
} from "@/components/assistant/use-assistant";

/** Tailwind `lg` breakpoint (matches `screens.lg` = 1024px). */
const LG_QUERY = "(min-width: 1024px)";

/** Track a media-query match, SSR-safe and updating on viewport change. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * Hook the shell uses to read the rail's open state (so it can size the grid
 * column). Returns the full state so the same instance drives both the desktop
 * column and the mobile Sheet.
 */
export function useAssistantRail(): AssistantState {
  return useAssistantState();
}

interface AssistantRailProps {
  state: AssistantState;
}

/**
 * Renders ONE of two presentations off a single state object, chosen by the
 * `lg` media query:
 *   - the desktop column (`lg`+), or
 *   - the mobile overlay Sheet (`<lg`).
 */
export function AssistantRail({ state }: AssistantRailProps) {
  const { open, setOpen, scope, setScope } = state;
  const isDesktop = useMediaQuery(LG_QUERY);

  const panel = (
    <AssistantPanel
      scope={scope}
      onScopeChange={setScope}
      onClose={() => setOpen(false)}
    />
  );

  if (isDesktop) {
    if (!open) return null;
    return (
      // A single border-l structural fold; no tint stacked on top.
      <aside
        data-testid="assistant-rail"
        aria-label="Assistant"
        className="hidden w-[480px] shrink-0 border-l border-border bg-background lg:block xl:w-[560px]"
      >
        <div className="sticky top-0 h-screen">{panel}</div>
      </aside>
    );
  }

  // Tablet / mobile: an overlay Sheet, so <main> never reflows.
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        // Hide the Sheet's built-in close button — the AssistantPanel header
        // already renders the single close affordance (avoids a duplicate X).
        className="w-[92vw] max-w-[560px] p-0 sm:max-w-[560px] [&>button]:hidden"
        data-testid="assistant-sheet"
      >
        {/* Accessible title/description for the dialog; visually hidden. */}
        <SheetTitle className="sr-only">Assistant</SheetTitle>
        <SheetDescription className="sr-only">
          The AOS assistant — ask about entities, run searches, and approve or
          reject proposed changes.
        </SheetDescription>
        {panel}
      </SheetContent>
    </Sheet>
  );
}

export default AssistantRail;
